// src/services/pageActionPermission/PageActionPermission.ts
// Class dùng chung để check hiển thị (button) / enable (input) theo phân quyền action-button/input
// của Role hiện tại. Trước đây cache kết quả vào localStorage ("pageActionPermissionCodes"/
// "pageActionPermissionFullAccess") — người dùng có thể F12 sửa trực tiếp để tự mở khoá toàn bộ
// button/input (đặt isFullAccess = true), nên đổi sang chỉ giữ trong bộ nhớ JS (không persist ra
// localStorage).
// Từ mục 9, README_SERCURITY.md: dữ liệu được nạp bởi slices/permission/thunk.ts#fetchMyPermissions
// (Redux global state) — class này chỉ còn đóng vai trò cache đọc nhanh trong bộ nhớ cho 60 file
// trong pages/** đang gọi canDisplay/canEnable/canDisplayAny trực tiếp, không tự gọi API nữa.

import { buildPageActionCode } from 'services/pageActionScanner/buildActionCode';

export class PageActionPermission {
  private static allowedCodes: Set<string> = new Set();
  private static isFullAccess = false;

  /**
   * Lưu kết quả `me/page-action-input-codes` vào bộ nhớ (KHÔNG persist ra localStorage) — gọi bởi
   * slices/permission/thunk.ts#fetchMyPermissions mỗi khi Redux fetch lại quyền (login/F5/mở lại
   * trình duyệt/gặp 403). Role IsProtected (backend) trả về isFullAccess=true, codes rỗng — không
   * cần so khớp từng code nữa, mọi canDisplay/canEnable trả về true ngay.
   */
  static setAllowedCodes(data: { isFullAccess: boolean; codes: string[] }): void {
    this.isFullAccess = data.isFullAccess;
    this.allowedCodes = new Set(data.codes);
  }

  /**
   * Xóa dữ liệu đã lưu — gọi lúc logout.
   */
  static clear(): void {
    this.allowedCodes = new Set();
    this.isFullAccess = false;
  }

  /**
   * Cờ isFullAccess (Role.IsProtected = true ở system-service).
   */
  static getIsFullAccess(): boolean {
    return this.isFullAccess;
  }

  private static isAllowed(urlPage: string, elementCode: string): boolean {
    if (this.getIsFullAccess()) return true;

    const code = buildPageActionCode(urlPage, elementCode);
    return this.allowedCodes.has(code);
  }

  /**
   * Dùng cho button: false → ẩn hẳn, không render.
   */
  static canDisplay(urlPage: string, elementCode: string): boolean {
    return this.isAllowed(urlPage, elementCode);
  }

  /**
   * Dùng cho input: false → disable, vẫn hiển thị.
   */
  static canEnable(urlPage: string, elementCode: string): boolean {
    return this.isAllowed(urlPage, elementCode);
  }

  /**
   * Dùng để ẩn cả cột "Thao tác" của 1 bảng: true nếu role có quyền với ít nhất 1 action-code
   * trong tập codes truyền vào (xét tĩnh theo cả cột, không phụ thuộc trạng thái từng dòng).
   */
  static canDisplayAny(urlPage: string, elementCodes: string[]): boolean {
    return elementCodes.some((code) => this.isAllowed(urlPage, code));
  }
}
